import Head from 'next/head';

import { RequestUrl } from '../apolloClient/types';
import { IS_PROD } from '../src/constants';
import { toCanonicalUrl } from 'src/utils/canonical';
import RealEstateAgentSchema from './Schema/RealEstateAgentSchema';

type Props = {
  title: string;
  url: RequestUrl;
  description?: string;
  metaKeywords?: string[];
  keywords?: string;
  image?: string;
  canonicalUrl?: RequestUrl;
  noCanonical?: boolean;
  robots?: string;
};

const SITE_NAME = 'CondoBlackBook';
const AUTHOR = 'Condo Black Book, LLC';

export const Metadata = (props: Props) => {
  const metaKeywords = (props.metaKeywords || []).join(', ') || props.keywords;
  // An explicit `canonicalUrl` is used verbatim (e.g. the blog tag page passes
  // its server-resolved slug). Otherwise the page canonicalises to its own
  // `url`, made absolute by toCanonicalUrl (the query is kept).
  const canonicalHref = props.canonicalUrl || toCanonicalUrl(props.url);

  let metaRobots = 'index, follow';

  if (!IS_PROD) {
    metaRobots = 'noindex, nofollow';
  } else if (props.url?.includes('/search')) {
    metaRobots = props.robots || 'index, follow';
  }

  return (
    <>
      <Head>
        <title>{props.title}</title>
        <meta name="robots" content={metaRobots} />

        <meta httpEquiv="Content-Type" content="text/html;charset=utf-8" />
        <meta
          httpEquiv="X-UA-Compatible"
          content="IE=10; IE=9; IE=8; IE=7; IE=EDGE"
        />
        <meta name="title" content={props.title} />
        {metaKeywords && <meta name="keywords" content={metaKeywords} />}
        {props.description && (
          <meta name="description" content={props.description} />
        )}
        <meta name="author" content={AUTHOR} />
        <meta
          name="copyright"
          content={`Copyright (c) 2007-${new Date().getFullYear()} Condo Black Book, LLC`}
        />
        <meta name="application-name" content={SITE_NAME} />
        <meta property="og:site_name" content={SITE_NAME} />
        <meta property="og:title" content={props.title} />
        <meta property="og:type" content="website" />
        {props.url && <meta property="og:url" content={props.url} />}
        {props.description && (
          <meta property="og:description" content={props.description} />
        )}
        <meta property="og:image" content={props.image} />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:site" content={SITE_NAME} />
        <meta name="twitter:title" content={props.title} />
        <meta name="twitter:creator" content="@condoblackbook" />
        {props.url && <meta name="twitter:url" content={props.url} />}
        {props.description && (
          <meta name="twitter:description" content={props.description} />
        )}
        <meta name="twitter:image" content={props.image} />
        {!props.noCanonical && canonicalHref && (
          <link rel="canonical" href={canonicalHref} />
        )}
      </Head>
      <RealEstateAgentSchema />
    </>
  );
};
